<script setup lang="ts"></script>
<template>
    <ContentPage title="Prohlášení o přístupnosti Fleequid">
        <section class="mb-8">
            <p class="mt-3 text-base">
                Fleequid (Adorea SRL) se zavazuje zpřístupnit své webové stránky v souladu se zákonem č. 4 z 9. ledna 2004. Toto prohlášení o
                přístupnosti se vztahuje na:
            </p>
            <p class="mt-2">
                <NuxtLink class="text-primary hover:underline" to="https://fleequid.com" target="_blank" external>https://fleequid.com</NuxtLink>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Přístupnost</h2>
            <p class="mt-2">
                Naším cílem je zpřístupnit digitální obsah všem lidem, včetně osob se zdravotním postižením. Zavazujeme se neustále zlepšovat
                uživatelskou zkušenost a přijímat nejlepší postupy v oblasti přístupnosti, abychom zajistili spravedlivý a inkluzivní přístup.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Prohlášení o přístupnosti</h2>
            <p class="mt-2">
                V souladu s platnými předpisy a v souladu s našimi hodnotami inkluze a rovných příležitostí jsme připravili oficiální Prohlášení o
                přístupnosti. V dokumentu je popsána úroveň přístupnosti webu a kroky podniknuté k dodržení Web Content Accessibility Guidelines
                (WCAG). Úplné prohlášení je k dispozici k nahlédnutí a stažení prostřednictvím níže uvedeného odkazu:
            </p>
            <p class="mt-2">
                <a class="text-primary hover:underline" href="/docs/accessibility/accessibility-statement_CS.pdf" target="_blank">
                    Prohlášení o přístupnosti Fleequid
                </a>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Stav shody</h2>
            <p class="mt-2">Částečně vyhovuje</p>
            <p class="mt-3">
                Tento web je částečně v souladu s požadavky uvedenými v příloze A normy UNI CEI EN 301549 z důvodu následujících případů nevyhovění.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Nedostupný obsah</h2>
            <p class="mt-3">Následující obsah není přístupný z důvodu nedodržení zákona č. 4 z 9. ledna 2004.:</p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Pro webové stránky</h3>
            <h4 class="text-highlighted mt-3 font-medium">Obecně:</h4>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>1.3.1 Informace a korelace (Úroveň A)</li>
                <li>1.4.3 Minimální kontrast (Úroveň AA)</li>
                <li>1.4.4 Změna velikosti textu (Úroveň AA)</li>
                <li>1.4.10 Přepočet toku (pouze úroveň AA 2.1)</li>
                <li>2.4.1 Skoky bloků (Úroveň A)</li>
                <li>2.4.4 Účel odkazu (v kontextu) (Úroveň A)</li>
                <li>2.4.5 Různé režimy (Úroveň AA)</li>
                <li>3.3.1 Identifikace chyb (Úroveň A)</li>
                <li>4.1.2 Název, role, hodnota (Úroveň A)</li>
                <li>4.1.3 Stavové zprávy (Úroveň AA wcag 2.1)</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Vypracování prohlášení o přístupnosti</h2>
            <p class="mt-3">Toto prohlášení bylo vypracováno <time datetime="2025-08-20" class="font-bold">20. srpna 2025</time>.</p>
            <p class="mt-2">
                Informace uvedené v tomto prohlášení o přístupnosti byly získány na základě hodnocení provedeného třetími stranami, pomocí
                automatických testovacích systémů a prostřednictvím přímé manuální kontroly odborníkem na přístupnost.
            </p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Nástroje použité pro automatické ověření:</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Deque Axe Accessibility Checker (rozšíření pro Firefox a Chrome)</li>
                <li>Siteimprove Accessibility Checker</li>
                <li>WCAG kontrola barevného kontrastu</li>
            </ul>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Asistivní technologie:</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Čtečka obrazovky: NVDA</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Zpětná vazba a kontaktní informace</h2>
            <p class="mt-3">Případné oznámení lze kdykoli zaslat přímo na e-mail: <FleequidEmailLink type="accessibility" /></p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Způsob zasílání oznámení na AgID</h2>
            <p class="mt-3">
                V případě neuspokojivé odpovědi nebo neodpovědi do třiceti dnů od oznámení nebo žádosti, může dotčená osoba zaslat oznámení využitím
                způsobu uvedeného na oficiálním webu AgID.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Informace o webu</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Datum zveřejnění webu:</dt>
                    <dd class="sm:col-span-2">
                        <time datetime="2024-08-27" class="font-bold">27. srpna 2024</time>
                    </dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Byly provedeny testy použitelnosti:</dt>
                    <dd class="font-bold sm:col-span-2">ne</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">CMS použité pro web:</dt>
                    <dd class="font-bold sm:col-span-2">Vlastní platforma / vlastní vývoj</dd>
                </div>
            </dl>
        </section>

        <section class="mb-4">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Informace o struktuře</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Počet zaměstnanců se zdravotním postižením v administraci:</dt>
                    <dd class="font-bold sm:col-span-2">1</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Počet pracovních míst pro zaměstnance se zdravotním postižením:</dt>
                    <dd class="font-bold sm:col-span-2">0</dd>
                </div>
            </dl>
        </section>
    </ContentPage>
</template>
